import React, { useMemo } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { ColumnEditorOptions } from "primereact/column";

type ScenarioItem = {
	tender_scenario_id: number;
	country: string;
	article: string;
	supplier_name: string;
	allocation_pct: number;
	volume: number | null;
	volume_last_year: number | null;
	incoterm: string | null;
	cpv_last_year: number | null;
	current_bid_price: number | null;
	previous_bid_price: number | null;
	target_cpv: number;
	currency: string | null;
};

type GroupData = {
	country: string;
	article: string;
	items: ScenarioItem[];
};

type Props = {
	group: GroupData;
	isDefaultScenario: boolean;
	editedCells: Map<number, number>;
	onCellEdit: (tender_scenario_id: number, newValue: string, originalValue: number) => void;
	getAllocationValue: (item: ScenarioItem) => number;
	isCellEdited: (item: ScenarioItem) => boolean;
	isCellSaved: (item: ScenarioItem) => boolean;
	isAllocationInvalid: (item: ScenarioItem) => boolean;
	formatCurrency: (value: number | null | undefined) => string;
	formatVolume: (value: number | null | undefined) => string;
	formatPercentage: (value: number | null | undefined) => string;
};

export default function ScenarioGroupTable({
	group,
	isDefaultScenario,
	editedCells,
	onCellEdit,
	getAllocationValue,
	isCellEdited,
	isCellSaved,
	isAllocationInvalid,
	formatCurrency,
	formatVolume,
	formatPercentage,
}: Props) {
	// Calculate total allocation for this group
	const totalAllocation = useMemo(() => {
		return group.items.reduce((sum, item) => {
			const allocation = editedCells.has(item.tender_scenario_id)
				? editedCells.get(item.tender_scenario_id)!
				: item.allocation_pct;
			return sum + (allocation || 0);
		}, 0);
	}, [group.items, editedCells]);

	// Check if allocation is invalid (not summing to 100%)
	const isInvalid = Math.abs(totalAllocation - 100) > 0.01;

	const textEditor = (options: ColumnEditorOptions) => {
		const rowData = options.rowData;
		const originalValue = rowData.allocation_pct;

		return (
			<InputText
				type="number"
				value={options.value}
				onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
					options.editorCallback!(e.target.value);
				}}
				onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
					onCellEdit(rowData.tender_scenario_id, e.target.value, originalValue);
				}}
				onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
					if (e.key === 'Enter') {
						e.preventDefault();
						const target = e.target as HTMLInputElement;
						onCellEdit(rowData.tender_scenario_id, target.value, originalValue);
						target.blur();
					}
				}}
				style={{ width: "100%" }}
				autoFocus
			/>
		);
	};

	return (
		<div
			className="scenario-group-table"
			style={{
				marginBottom: "0.75rem",
				border: isInvalid ? "2px solid var(--status-error)" : "1px solid var(--border-muted)",
				borderRadius: "6px",
				overflow: "hidden",
				backgroundColor: "var(--surface-default)",
			}}
		>
			{/* Group Header */}
			<div
				style={{
					backgroundColor: isInvalid ? "var(--color-red-50)" : "var(--surface-background)",
					padding: "0.5rem 0.75rem",
					borderBottom: isInvalid ? "2px solid var(--status-error)" : "1px solid var(--border-muted)",
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
				}}
			>
				<div>
					<h4 style={{ margin: 0, fontSize: "0.95rem", fontWeight: 600, color: "var(--text-primary)" }}>
						{group.article}
					</h4>
					<div style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginTop: "0.15rem" }}>
						{group.country}
					</div>
				</div>
				<div style={{ textAlign: "right" }}>
					<div
						style={{
							fontSize: "0.75rem",
							color: "var(--text-muted)",
							marginBottom: "0.15rem",
						}}
					>
						Total Allocation
					</div>
					<div
						style={{
							fontSize: "1rem",
							fontWeight: "bold",
							color: isInvalid ? "var(--status-error)" : totalAllocation === 100 ? "var(--status-success)" : "var(--status-warning)",
						}}
					>
						{totalAllocation.toFixed(2)}%
					</div>
					{isInvalid && (
						<div style={{ fontSize: "0.7rem", color: "var(--status-error)", marginTop: "0.15rem" }}>
							Must equal 100%
						</div>
					)}
				</div>
			</div>

			{/* Data Table */}
			<DataTable
				value={group.items}
				scrollable
				editMode={isDefaultScenario ? undefined : "cell"}
				dataKey="tender_scenario_id"
				showGridlines
				size="small"
			>
				<Column
					field="supplier_name"
					header="Supplier"
					style={{ minWidth: "200px", fontWeight: 500 }}
				/>
				<Column
					field="allocation_pct"
					header="Allocation"
					style={{ minWidth: "120px" }}
					body={(rowData) => {
						const value = getAllocationValue(rowData);
						const isEdited = isCellEdited(rowData);
						const isSaved = isCellSaved(rowData);
						const isInvalid = isAllocationInvalid(rowData);

						// Determine background color based on state priority:
						// 1. Invalid (red) - highest priority
						// 2. Edited (yellow) - currently being edited
						// 3. Saved (green) - recently saved
						// 4. Editable (light blue) - can be edited
						let backgroundColor = "transparent";
						let textColor = "inherit";

						if (isInvalid) {
							backgroundColor = "var(--color-red-50)";
							textColor = "var(--status-error)";
						} else if (isEdited) {
							backgroundColor = "var(--color-amber-100)";
						} else if (isSaved) {
							backgroundColor = "var(--color-green-100)";
							textColor = "var(--color-green-700)";
						} else if (!isDefaultScenario) {
							backgroundColor = "var(--color-blue-100)";
							textColor = "var(--color-blue-500)";
						}

						return (
							<span
								style={{
									backgroundColor,
									color: textColor,
									padding: "0.25rem 0.5rem",
									borderRadius: "4px",
									display: "inline-block",
									fontWeight: isEdited || isSaved ? "bold" : "normal",
									cursor: !isDefaultScenario ? "pointer" : "default",
								}}
							>
								{formatPercentage(value)}
							</span>
						);
					}}
					editor={(options) => textEditor(options)}
				/>
				<Column
					field="volume_last_year"
					header="Volume (last year)"
					style={{ minWidth: "150px" }}
					body={(rowData) => formatVolume(rowData.volume_last_year)}
				/>
				<Column
					field="volume"
					header="Total Volume Offered"
					style={{ minWidth: "180px" }}
					body={(rowData) => formatVolume(rowData.volume)}
				/>
				<Column
					field="previous_bid_price"
					header="Previous Bid Price"
					style={{ minWidth: "150px" }}
					body={(rowData) => formatCurrency(rowData.previous_bid_price)}
				/>
				<Column
					field="current_bid_price"
					header="Current Bid Price"
					style={{ minWidth: "150px" }}
					body={(rowData) => formatCurrency(rowData.current_bid_price)}
				/>
				<Column
					field="incoterm"
					header="Inco Terms"
					style={{ minWidth: "120px" }}
				/>
				<Column
					field="cpv_last_year"
					header="CPV (last year)"
					style={{ minWidth: "150px" }}
					body={(rowData) => formatCurrency(rowData.cpv_last_year)}
				/>
				<Column
					field="target_cpv"
					header="Target CPV"
					style={{ minWidth: "150px" }}
					body={(rowData) => formatCurrency(rowData.target_cpv)}
				/>
			</DataTable>
		</div>
	);
}
